import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, RefreshCw, AlertTriangle, AlertCircle, Award, ChevronDown, ChevronUp, ExternalLink, Wand2, CheckCircle2, XCircle, History as HistoryIcon, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const gradeColor = (g) => {
  if (!g) return 'text-slate-400';
  if (g.startsWith('A')) return 'text-emerald-400';
  if (g.startsWith('B')) return 'text-lime-400';
  if (g === 'C') return 'text-yellow-400';
  if (g === 'D') return 'text-orange-400';
  return 'text-red-500';
};

const gradeBadgeBg = (g) => {
  if (!g) return 'bg-slate-800';
  if (g.startsWith('A')) return 'bg-emerald-950 border-emerald-500/40';
  if (g.startsWith('B')) return 'bg-lime-950 border-lime-500/40';
  if (g === 'C') return 'bg-yellow-950 border-yellow-500/40';
  if (g === 'D') return 'bg-orange-950 border-orange-500/40';
  return 'bg-red-950 border-red-500/50';
};

const CHECK_LABELS = {
  direct_answer: 'Direct Answer',
  takeaways: 'Main Takeaways',
  author_bio: 'Author Bio',
  faq_section: 'FAQ Section',
  faq_schema: 'FAQ Schema',
  article_schema: 'Article Schema',
  internal_links: 'Internal Links',
  external_references: 'External References',
  sanskrit_attribution: 'Sanskrit Attribution',
  shastra_references: 'Shastra References',
  eeat_signals: 'EEAT Signals',
  word_count: 'Word Count',
  heading_structure: 'Heading Structure',
  featured_snippet: 'Featured Snippet',
  voice_search: 'Voice Search',
  ai_search_readiness: 'AI Search Ready',
  chatgpt_optimization: 'ChatGPT Optimized',
  gemini_optimization: 'Gemini Optimized',
  perplexity_optimization: 'Perplexity Optimized',
};

export default function EEATAuditDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [expandedBlog, setExpandedBlog] = useState(null);
  const [blogDetails, setBlogDetails] = useState({});
  const [sortBy, setSortBy] = useState('overall_score');
  const [sortAsc, setSortAsc] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'critical' | 'warning' | 'passing'
  const [fixModal, setFixModal] = useState(null); // { fix_id, slug, ... }
  const [fixGenLoading, setFixGenLoading] = useState({}); // { slug: bool }
  const [fixActLoading, setFixActLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await axios.get(`${API}/api/admin/eeat-audit`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sortedBlogs = useMemo(() => {
    if (!data?.blogs) return [];
    let arr = [...data.blogs];
    if (filter === 'critical') arr = arr.filter(b => b.critical_count > 0);
    else if (filter === 'warning') arr = arr.filter(b => b.critical_count === 0 && b.warning_count > 0);
    else if (filter === 'passing') arr = arr.filter(b => b.critical_count === 0 && b.warning_count === 0);
    arr.sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy];
      const cmp = typeof av === 'string' ? String(av).localeCompare(String(bv)) : (av - bv);
      return sortAsc ? cmp : -cmp;
    });
    return arr;
  }, [data, sortBy, sortAsc, filter]);

  const toggleExpand = async (slug) => {
    if (expandedBlog === slug) { setExpandedBlog(null); return; }
    setExpandedBlog(slug);
    if (!blogDetails[slug]) {
      try {
        const res = await axios.get(`${API}/api/admin/eeat-audit/blog/${slug}`, { withCredentials: true });
        setBlogDetails(prev => ({ ...prev, [slug]: res.data }));
      } catch (_) { /* silent */ }
    }
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortAsc(!sortAsc);
    else { setSortBy(col); setSortAsc(false); }
  };

  const generateFix = async (slug) => {
    setFixGenLoading(prev => ({ ...prev, [slug]: true }));
    try {
      // 🩹 Feb 07, 2026 P0 — Async / Background-Job Mode.
      // Cloudflare's 100s edge timeout would kill sync LLM calls (6+ x 15-30s
      // each). We now enqueue the job and poll status every 3s (up to 3 min).
      const res = await axios.post(
        `${API}/api/admin/eeat-fix/generate`, { slug }, { withCredentials: true },
      );
      const fixId = res.data.id;

      // Poll status endpoint until terminal state or timeout
      const POLL_INTERVAL_MS = 3000;
      const MAX_POLLS = 60;  // 3 minutes max
      let polls = 0;
      let finalDoc = null;
      while (polls < MAX_POLLS) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
        polls += 1;
        try {
          const status = await axios.get(
            `${API}/api/admin/eeat-fix/status/${fixId}`, { withCredentials: true },
          );
          const st = status.data.status;
          if (st === 'pending') {
            // Success — load full doc with preview_html rendered
            const full = await axios.get(
              `${API}/api/admin/eeat-fix/${fixId}`, { withCredentials: true },
            );
            finalDoc = full.data;
            break;
          }
          if (st === 'generation_failed') {
            throw new Error(status.data.error || 'LLM generation failed');
          }
          // queued / processing → keep polling
        } catch (pollErr) {
          // Bubble non-404 errors immediately
          if (pollErr?.response?.status && pollErr.response.status !== 404) {
            throw pollErr;
          }
        }
      }
      if (!finalDoc) {
        throw new Error('Fix generation timed out after 3 minutes. Try again — LLM may be busy.');
      }
      setFixModal(finalDoc);
    } catch (e) {
      alert(`Fix generate failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setFixGenLoading(prev => ({ ...prev, [slug]: false }));
    }
  };

  const cleanupShlokas = async (slug) => {
    setFixGenLoading(prev => ({ ...prev, [slug + '-clean']: true }));
    try {
      const res = await axios.post(`${API}/api/admin/eeat-fix/cleanup-shlokas`, { slug }, { withCredentials: true });
      if (res.data.no_changes) {
        alert(`✅ No unverified shlokas found in this blog. Nothing to clean up.\n(${res.data.shlokas_scanned} shlokas scanned, all verified)`);
        return;
      }
      // Load full doc with preview_html
      const full = await axios.get(`${API}/api/admin/eeat-fix/${res.data.id}`, { withCredentials: true });
      setFixModal(full.data);
    } catch (e) {
      alert(`Cleanup failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setFixGenLoading(prev => ({ ...prev, [slug + '-clean']: false }));
    }
  };

  const approveFix = async () => {
    if (!fixModal) return;
    setFixActLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/eeat-fix/${fixModal.id}/approve`, {}, { withCredentials: true });
      alert(`✅ Applied!  Score: ${res.data.score_before} → ${res.data.score_after}  (+${res.data.improvement})`);
      setFixModal(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const errs = detail?.errors ? '\n' + detail.errors.slice(0, 5).map(x => '• ' + x).join('\n') : '';
      alert(`❌ Apply blocked by validators — blog NOT modified.\n${detail?.message || detail || e.message}${errs}`);
    } finally {
      setFixActLoading(false);
    }
  };

  const rejectFix = async () => {
    if (!fixModal) return;
    const reason = prompt('Reject reason (optional):') || '';
    setFixActLoading(true);
    try {
      await axios.post(`${API}/api/admin/eeat-fix/${fixModal.id}/reject`, { reason }, { withCredentials: true });
      setFixModal(null);
    } catch (e) {
      alert(`Reject failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setFixActLoading(false);
    }
  };

  const openHistory = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/eeat-fix/history`, { withCredentials: true });
      setHistory(res.data.history || []);
      setHistoryOpen(true);
    } catch (e) {
      alert(`History load failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // 🚀 Feb 08, 2026 P0 — One-click Bulk Fix All (auto-approve if validator passes)
  const [bulkJob, setBulkJob] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const runBulkMigrate = async () => {
    if (!window.confirm(
      "🚀 Bulk Fix All: सभी below-60-score blogs पर fixes generate होकर auto-approve हो जाएंगे " +
      "(जो validator pass करें)। Failed ones manual review में जाएंगे। " +
      "Ye action 5-15 min लेगा। Continue?"
    )) return;
    setBulkRunning(true); setBulkJob(null);
    try {
      const startRes = await axios.post(
        `${API}/api/admin/eeat-fix/bulk-migrate`,
        { max_score: 60 },
        { withCredentials: true },
      );
      const jobId = startRes.data.job_id;
      const total = startRes.data.total_blogs_to_process;
      setBulkJob({ jobId, total, progress: 0, message: `${total} blogs को queue किया गया…` });

      // Poll every 4s
      for (let i = 0; i < 300; i++) {  // max 20 min
        await new Promise(r => setTimeout(r, 4000));
        const s = await axios.get(`${API}/api/admin/eeat-fix/bulk-migrate/status/${jobId}`, { withCredentials: true });
        const d = s.data;
        setBulkJob({
          jobId, total: d.total_blogs,
          progress: d.progress?.current || 0,
          message: d.progress?.message || d.status,
          autoApproved: d.auto_approved || 0,
          validationFailed: d.validation_failed || 0,
          errors: d.errors || 0,
          status: d.status,
          results: d.results || [],
        });
        if (d.status === 'completed' || d.status === 'failed') break;
      }
      // Refresh dashboard
      await load();
    } catch (e) {
      alert(`Bulk migrate failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBulkRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center text-slate-300" data-testid="eeat-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 mx-auto animate-spin text-yellow-400" />
          <p className="mt-3 text-sm">EEAT audit चला रहे हैं… सभी blogs scan हो रहे हैं…</p>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center text-red-400 p-4" data-testid="eeat-error">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 mx-auto mb-3" />
          <p>{err}</p>
          <button onClick={load} className="mt-4 px-4 py-2 bg-yellow-600 text-slate-950 rounded font-semibold" data-testid="eeat-retry">Retry</button>
        </div>
      </div>
    );
  }

  const s = data?.summary || {};

  return (
    <div className="min-h-screen bg-[#0F172A] text-slate-100 p-4 md:p-8" data-testid="eeat-dashboard">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6 flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 transition-colors"
          data-testid="eeat-back-btn"
        >
          <ArrowLeft className="w-5 h-5" /> Admin Dashboard
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={runBulkMigrate}
            disabled={bulkRunning}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg font-black shadow-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="eeat-bulk-migrate-btn"
          >
            {bulkRunning
              ? <RefreshCw className="w-4 h-4 animate-spin" />
              : <Sparkles className="w-4 h-4" />}
            {bulkRunning ? 'Bulk Fixing…' : '🚀 Bulk Fix All'}
          </button>
          <button
            onClick={openHistory}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg font-semibold border border-slate-700 transition-colors"
            data-testid="eeat-history-btn"
          >
            <HistoryIcon className="w-4 h-4" /> Audit History
          </button>
          <button
            onClick={load}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-slate-950 rounded-lg font-bold shadow-lg transition-colors"
            data-testid="eeat-refresh-btn"
          >
            <RefreshCw className="w-4 h-4" /> Refresh Audit
          </button>
        </div>
      </div>

      {/* 🚀 Bulk Migrate Progress Panel */}
      {bulkJob && (
        <div className="max-w-7xl mx-auto mb-6" data-testid="bulk-migrate-panel">
          <div className="rounded-xl border-2 border-purple-500/40 bg-gradient-to-br from-purple-950/60 to-slate-900/60 p-4 backdrop-blur">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-black text-purple-200 flex items-center gap-2">
                🚀 Bulk Migration Progress
                <span className={`text-xs px-2 py-0.5 rounded ${bulkJob.status === 'completed' ? 'bg-emerald-600' : bulkJob.status === 'failed' ? 'bg-red-600' : 'bg-amber-600'} text-white`}>
                  {bulkJob.status || 'running'}
                </span>
              </h3>
              <div className="text-xs text-slate-300 font-mono">
                {bulkJob.progress || 0} / {bulkJob.total || '?'}
              </div>
            </div>
            <p className="text-xs text-slate-300 mb-2 truncate">{bulkJob.message}</p>
            <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 via-yellow-400 to-purple-500 transition-all"
                style={{ width: bulkJob.total ? `${Math.min(100, (bulkJob.progress / bulkJob.total) * 100)}%` : '0%' }}
              />
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3 text-center text-xs">
              <div className="bg-emerald-950/40 rounded p-2 border border-emerald-500/30">
                <div className="text-emerald-300 font-black text-xl">{bulkJob.autoApproved || 0}</div>
                <div className="text-slate-400">Auto-approved</div>
              </div>
              <div className="bg-amber-950/40 rounded p-2 border border-amber-500/30">
                <div className="text-amber-300 font-black text-xl">{bulkJob.validationFailed || 0}</div>
                <div className="text-slate-400">Needs manual review</div>
              </div>
              <div className="bg-red-950/40 rounded p-2 border border-red-500/30">
                <div className="text-red-300 font-black text-xl">{bulkJob.errors || 0}</div>
                <div className="text-slate-400">Errors</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overall Score Hero */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className={`rounded-2xl border-2 ${gradeBadgeBg(s.grade)} p-6 md:p-10 shadow-2xl`}>
          <div className="flex flex-wrap items-center gap-6 justify-between">
            <div>
              <p className="text-sm uppercase tracking-widest text-slate-400 mb-2">Website EEAT Score</p>
              <div className="flex items-baseline gap-3">
                <span
                  className={`text-6xl md:text-7xl font-black ${gradeColor(s.grade)}`}
                  data-testid="eeat-website-score"
                >
                  {s.website_score ?? 0}
                </span>
                <span className="text-2xl text-slate-500">/ 100</span>
                <span
                  className={`ml-3 px-3 py-1 rounded-full text-lg font-black border-2 ${gradeBadgeBg(s.grade)} ${gradeColor(s.grade)}`}
                  data-testid="eeat-website-grade"
                >
                  {s.grade}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center flex-1 min-w-[260px]">
              <StatBox label="Total Blogs" value={s.total_blogs} testid="eeat-stat-total" />
              <StatBox label="Average Score" value={s.average_score} testid="eeat-stat-avg" />
              <StatBox label="Critical Issues" value={s.critical_issues_count} color="text-red-400" testid="eeat-stat-critical" />
              <StatBox label="Warning Issues" value={s.warning_issues_count} color="text-yellow-400" testid="eeat-stat-warning" />
            </div>
          </div>
        </div>
      </div>

      {/* Distribution + Top/Bottom */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card title="Grade Distribution" testid="eeat-distribution">
          <div className="space-y-2">
            {Object.entries(s.distribution || {}).map(([label, count]) => (
              <div key={label} className="flex items-center gap-3 text-sm">
                <span className="w-28 text-slate-400 shrink-0">{label}</span>
                <div className="flex-1 bg-slate-800 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-yellow-500"
                    style={{ width: `${s.total_blogs ? (count / s.total_blogs) * 100 : 0}%` }}
                  />
                </div>
                <span className="w-8 text-right font-semibold" data-testid={`eeat-dist-${label.split(' ')[0]}`}>{count}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="🏆 Top 10 Blogs" testid="eeat-top10">
          <TopBottomList items={s.top_10} navigate={navigate} testidPrefix="eeat-top" />
        </Card>

        <Card title="⚠️ Bottom 10 Blogs" testid="eeat-bottom10">
          <TopBottomList items={s.bottom_10} navigate={navigate} testidPrefix="eeat-bottom" />
        </Card>
      </div>

      {/* Improvement Suggestions */}
      <div className="max-w-7xl mx-auto mb-6">
        <Card title="🎯 Top Improvement Opportunities (site-wide)" testid="eeat-suggestions">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {(s.improvement_suggestions || []).map(x => (
              <div key={x.check} className="bg-slate-900/60 border border-slate-700 rounded-lg p-3">
                <p className="text-sm text-slate-300">{CHECK_LABELS[x.check] || x.check}</p>
                <p className="text-2xl font-bold text-orange-400 mt-1">{x.blogs_failing}</p>
                <p className="text-xs text-slate-500">blogs failing</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Per-blog Table */}
      <div className="max-w-7xl mx-auto">
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl">
          <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-slate-800">
            <h2 className="text-lg font-bold">All Blogs ({sortedBlogs.length})</h2>
            <div className="flex flex-wrap gap-2">
              {['all', 'critical', 'warning', 'passing'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    filter === f
                      ? 'bg-yellow-500 text-slate-950 border-yellow-500 font-bold'
                      : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                  }`}
                  data-testid={`eeat-filter-${f}`}
                >{f.toUpperCase()}</button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">#</th>
                  <SortableTH label="Score" col="overall_score" cur={sortBy} asc={sortAsc} onClick={toggleSort} />
                  <th className="text-left px-4 py-3">Title</th>
                  <SortableTH label="Grade" col="grade" cur={sortBy} asc={sortAsc} onClick={toggleSort} />
                  <SortableTH label="Critical" col="critical_count" cur={sortBy} asc={sortAsc} onClick={toggleSort} />
                  <SortableTH label="Warnings" col="warning_count" cur={sortBy} asc={sortAsc} onClick={toggleSort} />
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedBlogs.map((b, i) => (
                  <React.Fragment key={b.slug}>
                    <tr
                      className="border-t border-slate-800 hover:bg-slate-800/40"
                      data-testid={`eeat-row-${b.slug}`}
                    >
                      <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-xl font-black ${gradeColor(b.grade)}`}>{b.overall_score}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-md truncate" title={b.title}>{b.title || b.slug}</div>
                        {b.category && (
                          <span className="text-[11px] text-slate-500">{b.category}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded font-bold text-xs border ${gradeBadgeBg(b.grade)} ${gradeColor(b.grade)}`}>
                          {b.grade}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {b.critical_count > 0 ? (
                          <span className="inline-flex items-center gap-1 text-red-400 font-bold">
                            <AlertCircle className="w-3.5 h-3.5" /> {b.critical_count}
                          </span>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {b.warning_count > 0 ? (
                          <span className="inline-flex items-center gap-1 text-yellow-400">
                            <AlertTriangle className="w-3.5 h-3.5" /> {b.warning_count}
                          </span>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          {b.critical_count > 0 && (
                            <button
                              onClick={() => generateFix(b.slug)}
                              disabled={fixGenLoading[b.slug]}
                              className="text-xs font-bold text-emerald-300 hover:text-emerald-200 bg-emerald-950 border border-emerald-500/40 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50"
                              data-testid={`eeat-fix-${b.slug}`}
                              title="Generate LLM + deterministic fix suggestions"
                            >
                              {fixGenLoading[b.slug]
                                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                : <Wand2 className="w-3.5 h-3.5" />}
                              Fix
                            </button>
                          )}
                          <button
                            onClick={() => cleanupShlokas(b.slug)}
                            disabled={fixGenLoading[b.slug + '-clean']}
                            className="text-xs font-bold text-purple-300 hover:text-purple-200 bg-purple-950 border border-purple-500/40 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50"
                            data-testid={`eeat-cleanup-shlokas-${b.slug}`}
                            title="Scan & remove unverified Sanskrit shlokas (Shloka Cleanup Wizard)"
                          >
                            {fixGenLoading[b.slug + '-clean']
                              ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                              : <Sparkles className="w-3.5 h-3.5" />}
                            Cleanup Shlokas
                          </button>
                          <button
                            onClick={() => toggleExpand(b.slug)}
                            className="text-yellow-400 hover:text-yellow-300 text-xs font-semibold"
                            data-testid={`eeat-expand-${b.slug}`}
                          >
                            {expandedBlog === b.slug ? <ChevronUp className="w-4 h-4 inline" /> : <ChevronDown className="w-4 h-4 inline" />}
                            {' '}Details
                          </button>
                          <a
                            href={`/blog/${b.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-slate-400 hover:text-yellow-400"
                            title="Open blog"
                            data-testid={`eeat-open-${b.slug}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                    {expandedBlog === b.slug && (
                      <tr className="border-t border-slate-800 bg-slate-950/40">
                        <td colSpan={7} className="p-4">
                          <BlogChecks
                            slug={b.slug}
                            report={blogDetails[b.slug]}
                            criticalIssues={b.critical_issues}
                            warningIssues={b.warning_issues}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <p className="max-w-7xl mx-auto mt-6 text-xs text-slate-500 text-center">
        EEAT Audit • ViratAstro.cloud • Deterministic scoring against 19 EEAT/AI-search parameters • Feb 2026
      </p>

      {/* ── Fix Preview Modal (Compare View) ── */}
      {fixModal && (
        <FixPreviewModal
          fix={fixModal}
          onClose={() => setFixModal(null)}
          onApprove={approveFix}
          onReject={rejectFix}
          busy={fixActLoading}
        />
      )}

      {/* ── Audit History Drawer ── */}
      {historyOpen && (
        <div className="fixed inset-0 bg-black/70 z-40 flex items-end md:items-center justify-center p-4" onClick={() => setHistoryOpen(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="eeat-history-drawer">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                <HistoryIcon className="w-5 h-5 text-yellow-400" /> Audit History ({history.length})
              </h3>
              <button onClick={() => setHistoryOpen(false)} className="text-slate-400 hover:text-slate-100">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {history.length === 0 ? (
                <p className="p-8 text-center text-slate-500 text-sm">No audit events yet. Approve or reject a fix to populate history.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-950 text-slate-400 uppercase text-[10px]">
                    <tr>
                      <th className="text-left px-3 py-2">Timestamp</th>
                      <th className="text-left px-3 py-2">Action</th>
                      <th className="text-left px-3 py-2">Blog</th>
                      <th className="text-left px-3 py-2">Admin</th>
                      <th className="text-right px-3 py-2">Before → After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(h => (
                      <tr key={h.id} className="border-t border-slate-800">
                        <td className="px-3 py-2 text-slate-500">{h.timestamp?.slice(0, 19).replace('T', ' ')}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                            h.action === 'approved' ? 'bg-emerald-900 text-emerald-300' :
                            h.action === 'rejected' ? 'bg-red-900 text-red-300' :
                            'bg-slate-800 text-slate-400'
                          }`}>
                            {h.action?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-300 max-w-md truncate" title={h.title}>{h.title || h.slug}</td>
                        <td className="px-3 py-2 text-slate-500">{h.admin}</td>
                        <td className="px-3 py-2 text-right">
                          {h.action === 'approved' ? (
                            <span className="font-bold">
                              <span className="text-red-400">{h.score_before}</span>
                              {' → '}
                              <span className="text-emerald-400">{h.score_after}</span>
                              <span className="ml-2 text-emerald-300">(+{h.improvement})</span>
                            </span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
const StatBox = ({ label, value, color = 'text-slate-100', testid }) => (
  <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-widest text-slate-400">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color}`}>{value ?? 0}</p>
  </div>
);

const Card = ({ title, children, testid }) => (
  <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid={testid}>
    <h3 className="text-sm font-bold text-slate-200 mb-3">{title}</h3>
    {children}
  </div>
);

const TopBottomList = ({ items, navigate, testidPrefix }) => (
  <ul className="space-y-1.5">
    {(items || []).map((b, i) => (
      <li
        key={b.slug}
        className="flex items-center gap-2 text-xs hover:bg-slate-800/50 rounded px-2 py-1 cursor-pointer"
        onClick={() => window.open(`/blog/${b.slug}`, '_blank', 'noopener')}
        data-testid={`${testidPrefix}-${i}`}
      >
        <span className="text-slate-500 w-4">{i + 1}.</span>
        <span className={`font-black w-10 text-right ${gradeColor(b.grade)}`}>{b.overall_score}</span>
        <span className="truncate flex-1 text-slate-300">{b.title || b.slug}</span>
        {b.critical_count > 0 && (
          <span className="text-red-400 font-bold text-[10px] px-1.5 rounded bg-red-950/60">
            {b.critical_count}!
          </span>
        )}
      </li>
    ))}
  </ul>
);

const SortableTH = ({ label, col, cur, asc, onClick }) => (
  <th
    className="text-left px-4 py-3 cursor-pointer hover:text-yellow-400 select-none"
    onClick={() => onClick(col)}
    data-testid={`eeat-sort-${col}`}
  >
    {label}{cur === col ? (asc ? ' ↑' : ' ↓') : ''}
  </th>
);

// ─────────────────────────────────────────────────────────────
// Fix Preview Modal — side-by-side compare view (current vs suggested)
// Admin can Approve (applies fix + runs validators) or Reject (no DB change).
// ─────────────────────────────────────────────────────────────
const FixPreviewModal = ({ fix, onClose, onApprove, onReject, busy }) => {
  const [view, setView] = useState('components'); // 'components' | 'preview'
  const fixes = fix.fixes || {};
  const compKeys = Object.keys(fixes);
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 md:p-4" onClick={onClose}>
      <div
        className="bg-slate-900 border-2 border-yellow-500/30 rounded-xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="eeat-fix-modal"
      >
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-widest">Fix Preview • Admin Review</p>
            <h3 className="text-lg font-bold text-slate-100 truncate max-w-[600px]" title={fix.title}>{fix.title || fix.slug}</h3>
            <p className="text-xs text-slate-400">
              Current EEAT Score:{' '}
              <span className={fix.audit_before?.overall_score < 50 ? 'text-red-400' : 'text-yellow-400'}>
                {fix.audit_before?.overall_score} ({fix.audit_before?.grade})
              </span>
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setView('components')}
              className={`px-3 py-1.5 text-xs rounded ${view === 'components' ? 'bg-yellow-500 text-slate-950 font-bold' : 'bg-slate-800 text-slate-300'}`}
              data-testid="fix-view-components"
            >Component-wise</button>
            <button
              onClick={() => setView('preview')}
              className={`px-3 py-1.5 text-xs rounded ${view === 'preview' ? 'bg-yellow-500 text-slate-950 font-bold' : 'bg-slate-800 text-slate-300'}`}
              data-testid="fix-view-preview"
            >Merged HTML Preview</button>
            <button onClick={onClose} className="ml-3 text-slate-400 hover:text-slate-100" data-testid="fix-modal-close">✕</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* 🚨 LLM Generation Warnings (components that couldn't be auto-generated) */}
          {Array.isArray(fix.warnings) && fix.warnings.length > 0 && (
            <div className="mb-4 bg-amber-950/40 border border-amber-500/50 rounded-lg p-4" data-testid="fix-generation-warnings">
              <p className="text-sm font-bold text-amber-200 mb-2">
                ⚠️ Generation failed for {fix.warnings.length} component{fix.warnings.length > 1 ? 's' : ''} — manual edit required
              </p>
              <ul className="space-y-2">
                {fix.warnings.map((w, i) => (
                  <li key={i} className="text-xs bg-amber-950/60 border border-amber-800 rounded p-2" data-testid={`fix-warning-${w.component}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-amber-100">{w.label || w.component}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-red-900/60 text-red-200 uppercase">Skipped</span>
                    </div>
                    <p className="mt-1 text-amber-300/90 leading-relaxed">
                      <span className="text-slate-400">Reason:</span> {w.reason}
                    </p>
                    <p className="text-amber-200/80">
                      <span className="text-slate-400">Action:</span> {w.admin_action || 'Manual edit required.'}
                    </p>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[11px] text-amber-300/70">
                💡 These components were <b>excluded</b> from the merged HTML — no broken content will be injected. You can approve the remaining fixes as-is, or reject and manually edit this blog.
              </p>
            </div>
          )}

          {/* Shloka cleanup summary banner */}
          {fix.type === 'shloka_cleanup' && fix.cleanup_report && (
            <div className="mb-4 bg-purple-950/40 border border-purple-500/40 rounded-lg p-4" data-testid="fix-cleanup-report">
              <p className="text-sm font-bold text-purple-200 mb-2">🧹 Shloka Cleanup Wizard Report</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><span className="text-slate-400">Scanned:</span> <span className="font-bold text-slate-100">{fix.cleanup_report.shlokas_scanned}</span></div>
                <div><span className="text-slate-400">Verified kept:</span> <span className="font-bold text-emerald-300">{fix.cleanup_report.verified_kept?.length || 0}</span></div>
                <div><span className="text-slate-400">Unverified removed:</span> <span className="font-bold text-red-300">{fix.cleanup_report.unverified_removed?.length || 0}</span></div>
                <div><span className="text-slate-400">Scholarly ¶ added:</span> <span className="font-bold text-yellow-300">{fix.cleanup_report.scholarly_paragraph_added ? 'YES' : 'NO'}</span></div>
              </div>
              {fix.cleanup_report.unverified_removed?.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs text-purple-300 cursor-pointer hover:text-purple-200">View removed shlokas ({fix.cleanup_report.unverified_removed.length})</summary>
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {fix.cleanup_report.unverified_removed.map((u, i) => (
                      <div key={i} className="text-[11px] bg-red-950/40 border border-red-800 rounded p-2 font-mono text-red-200">
                        {u.sanskrit_preview || u.preview || JSON.stringify(u).slice(0, 200)}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <p className="mt-3 text-[11px] text-slate-400">
                💡 Switch to <b>Merged HTML Preview</b> tab to compare before/after content.
              </p>
            </div>
          )}
          {view === 'components' ? (
            <div className="space-y-3">
              {compKeys.length === 0 && fix.type !== 'shloka_cleanup' && (
                <p className="text-slate-500 text-sm">
                  {Array.isArray(fix.warnings) && fix.warnings.length > 0
                    ? 'All components failed generation — see warnings above. Reject this fix and manually edit the blog.'
                    : 'No fixable components identified for this blog.'}
                </p>
              )}
              {compKeys.length === 0 && fix.type === 'shloka_cleanup' && (
                <p className="text-slate-400 text-sm">
                  This is a whole-content cleanup (no per-component fixes).
                  Switch to <b>Merged HTML Preview</b> to review the final HTML that will replace the blog on approval.
                </p>
              )}
              {compKeys.map(k => {
                const v = fixes[k];
                const hasError = v && typeof v === 'object' && v.error;
                return (
                  <div key={k} className="bg-slate-950/60 border border-slate-800 rounded-lg p-3" data-testid={`fix-comp-${k}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-emerald-300">{CHECK_LABELS[k] || k}</span>
                      {hasError && (
                        <span className="text-[11px] text-red-400 bg-red-950/60 px-2 py-0.5 rounded">LLM ERROR</span>
                      )}
                    </div>
                    {hasError ? (
                      <p className="text-xs text-red-400">{v.error}</p>
                    ) : v?.schema ? (
                      <pre className="text-[11px] text-slate-300 overflow-x-auto bg-slate-900 p-2 rounded max-h-64">{JSON.stringify(v.schema, null, 2)}</pre>
                    ) : v?.html ? (
                      <div className="text-xs text-slate-200 bg-slate-900 p-2 rounded max-h-64 overflow-y-auto"
                            dangerouslySetInnerHTML={{ __html: v.html }} />
                    ) : (
                      <p className="text-xs text-slate-500">(no content)</p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-red-400 uppercase font-bold mb-2">Current (Before)</p>
                <div className="text-[11px] text-slate-300 bg-slate-950 p-3 rounded border border-red-900/40 max-h-[60vh] overflow-y-auto whitespace-pre-wrap font-mono">
                  {fix.current_html || '(empty)'}
                </div>
              </div>
              <div>
                <p className="text-xs text-emerald-400 uppercase font-bold mb-2">Suggested (After Merge)</p>
                <div className="text-[11px] text-slate-100 bg-slate-950 p-3 rounded border border-emerald-900/40 max-h-[60vh] overflow-y-auto whitespace-pre-wrap font-mono">
                  {fix.preview_html || '(empty)'}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-800 flex items-center justify-between bg-slate-950/60">
          <p className="text-[11px] text-slate-500 max-w-md">
            🛡️ Fact Validator + Intro Structure + Placeholder Validator will run on approve.
            If any fails, blog is NOT modified.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onReject}
              disabled={busy}
              className="px-4 py-2 bg-red-950 hover:bg-red-900 text-red-300 border border-red-500/40 rounded-lg font-semibold text-sm flex items-center gap-1 disabled:opacity-50"
              data-testid="fix-reject-btn"
            >
              <XCircle className="w-4 h-4" /> Reject
            </button>
            <button
              onClick={onApprove}
              disabled={busy || (compKeys.length === 0 && fix.type !== 'shloka_cleanup')}
              className="px-5 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-lg font-black text-sm flex items-center gap-1 disabled:opacity-50 shadow-lg"
              data-testid="fix-approve-btn"
            >
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Approve & Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BlogChecks = ({ slug, report, criticalIssues, warningIssues }) => {
  if (!report) {
    return <div className="text-slate-500 text-xs">Loading detailed report…</div>;
  }
  const checks = report.checks || {};
  return (
    <div className="space-y-3">
      {criticalIssues?.length > 0 && (
        <div className="p-3 bg-red-950/40 border border-red-800 rounded-lg">
          <p className="text-red-300 text-xs font-bold uppercase mb-1">🚨 Critical Issues</p>
          <div className="flex flex-wrap gap-1.5">
            {criticalIssues.map(c => (
              <span key={c} className="text-xs px-2 py-0.5 rounded bg-red-900/60 text-red-200 border border-red-700">
                {CHECK_LABELS[c] || c}
              </span>
            ))}
          </div>
        </div>
      )}
      {warningIssues?.length > 0 && (
        <div className="p-3 bg-yellow-950/40 border border-yellow-800 rounded-lg">
          <p className="text-yellow-300 text-xs font-bold uppercase mb-1">⚠️ Warnings</p>
          <div className="flex flex-wrap gap-1.5">
            {warningIssues.map(w => (
              <span key={w} className="text-xs px-2 py-0.5 rounded bg-yellow-900/60 text-yellow-200 border border-yellow-700">
                {CHECK_LABELS[w] || w}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {Object.entries(checks).map(([k, v]) => (
          <div key={k} className="flex items-start gap-2 text-xs p-2 bg-slate-900/60 rounded border border-slate-800">
            <span className={`w-1 h-full min-h-[24px] rounded ${v.pass ? 'bg-emerald-500' : 'bg-red-500'}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-200">{CHECK_LABELS[k] || k}</span>
                <span className={`font-bold ${v.score >= 70 ? 'text-emerald-400' : v.score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {v.score}
                </span>
              </div>
              <div className="text-slate-500 text-[11px] mt-0.5 truncate" title={v.detail}>{v.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
